import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Shield, Eye, EyeOff } from 'lucide-react';

interface SecuritySettings {
  cspViolations: CSPViolation[];
  securityHeaders: SecurityHeaders;
  authAttempts: AuthAttempt[];
  sessionSecurity: SessionSecurity;
  dataEncryption: boolean;
  biometricAuth: boolean;
  twoFactorAuth: boolean;
  securityAlerts: boolean;
}

interface CSPViolation {
  id: string;
  directive: string;
  blockedUri: string;
  timestamp: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface SecurityHeaders {
  'Content-Security-Policy': string;
  'X-Frame-Options': string;
  'X-Content-Type-Options': string;
  'Strict-Transport-Security': string;
  'Referrer-Policy': string;
}

interface AuthAttempt {
  id: string;
  timestamp: number;
  success: boolean;
  ipAddress: string;
  userAgent: string;
  method: 'password' | 'biometric' | '2fa' | 'sso';
  riskScore: number;
}

interface SessionSecurity {
  sessionId: string;
  lastActivity: number;
  ipAddress: string;
  deviceFingerprint: string;
  isSecure: boolean;
  sameSite: 'strict' | 'lax' | 'none';
}

interface SecurityContextType {
  settings: SecuritySettings;
  reportCSPViolation: (violation: Omit<CSPViolation, 'id' | 'timestamp'>) => void;
  validateInput: (input: string, type: 'url' | 'email' | 'json' | 'sql') => boolean;
  encryptSensitiveData: (data: string) => Promise<string>;
  decryptSensitiveData: (encryptedData: string) => Promise<string>;
  checkPasswordStrength: (password: string) => PasswordStrength;
  scanForVulnerabilities: () => Promise<SecurityScan>;
  enableSecurityFeature: (feature: keyof SecuritySettings) => void;
  getSecurityScore: () => number;
}

interface PasswordStrength {
  score: number;
  feedback: string[];
  isStrong: boolean;
}

interface SecurityScan {
  score: number;
  vulnerabilities: SecurityVulnerability[];
  recommendations: string[];
}

interface SecurityVulnerability {
  id: string;
  type: 'xss' | 'injection' | 'csrf' | 'weak-auth' | 'insecure-transport';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  fix: string;
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
};

interface Props {
  children: React.ReactNode;
}

export const SecurityProvider: React.FC<Props> = ({ children }) => {
  const [settings, setSettings] = useState<SecuritySettings>({
    cspViolations: [],
    securityHeaders: {
      'Content-Security-Policy':
        "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'",
      'X-Frame-Options': 'DENY',
      'X-Content-Type-Options': 'nosniff',
      'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
      'Referrer-Policy': 'strict-origin-when-cross-origin',
    },
    authAttempts: [],
    sessionSecurity: {
      sessionId: '',
      lastActivity: Date.now(),
      ipAddress: '',
      deviceFingerprint: '',
      isSecure: true,
      sameSite: 'strict',
    },
    dataEncryption: true,
    biometricAuth: false,
    twoFactorAuth: false,
    securityAlerts: true,
  });

  const cryptoKeyRef = useRef<CryptoKey | null>(null);

  // Initialize security features
  useEffect(() => {
    initializeSecurity();
    setupCSPMonitoring();
    generateDeviceFingerprint();
  }, []);

  const initializeSecurity = async () => {
    try {
      // Generate encryption key
      if (window.crypto && window.crypto.subtle) {
        cryptoKeyRef.current = await window.crypto.subtle.generateKey(
          { name: 'AES-GCM', length: 256 },
          false,
          ['encrypt', 'decrypt']
        );
      }

      // Check for biometric authentication support
      if ('credentials' in navigator && 'create' in navigator.credentials) {
        setSettings(prev => ({ ...prev, biometricAuth: true }));
      }

      // Initialize session security
      updateSessionSecurity();
    } catch (error) {
      console.error('Failed to initialize security features:', error);
    }
  };

  const setupCSPMonitoring = () => {
    // Listen for CSP violations
    document.addEventListener('securitypolicyviolation', event => {
      const violation: CSPViolation = {
        id: generateId(),
        directive: event.violatedDirective,
        blockedUri: event.blockedURI,
        timestamp: Date.now(),
        severity: calculateSeverity(event.violatedDirective),
      };

      reportCSPViolation(violation);
    });
  };

  const generateDeviceFingerprint = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);

      const fingerprint = btoa(
        JSON.stringify({
          canvas: canvas.toDataURL(),
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          screen: `${screen.width}x${screen.height}`,
        })
      );

      setSettings(prev => ({
        ...prev,
        sessionSecurity: {
          ...prev.sessionSecurity,
          deviceFingerprint: fingerprint,
        },
      }));
    }
  };

  const updateSessionSecurity = () => {
    setSettings(prev => ({
      ...prev,
      sessionSecurity: {
        ...prev.sessionSecurity,
        lastActivity: Date.now(),
        sessionId: generateSessionId(),
        ipAddress: 'client-side', // Would be set by server
        isSecure: location.protocol === 'https:',
      },
    }));
  };

  const reportCSPViolation = (violation: Omit<CSPViolation, 'id' | 'timestamp'>) => {
    const fullViolation: CSPViolation = {
      ...violation,
      id: generateId(),
      timestamp: Date.now(),
    };

    setSettings(prev => ({
      ...prev,
      cspViolations: [...prev.cspViolations, fullViolation].slice(-100), // Keep last 100
    }));

    // Report to security service
    if (settings.securityAlerts) {
      reportSecurityIncident('csp_violation', fullViolation);
    }
  };

  const validateInput = (input: string, type: 'url' | 'email' | 'json' | 'sql'): boolean => {
    const patterns = {
      url: /^https?:\/\/([\w-]+(\.[\w-]+)*)(:[0-9]{1,5})?(\/.*)?$/,
      email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      json: /^[\s]*[{[].*[}\]][\s]*$/,
      sql: /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION)\b)/i,
    };

    // XSS prevention
    const xssPattern = /<script|javascript:|vbscript:|data:text\/html|on\w+\s*=/i;
    if (xssPattern.test(input)) {
      reportSecurityIncident('xss_attempt', { input, type });
      return false;
    }

    // SQL injection prevention
    if (type === 'sql' && patterns.sql.test(input)) {
      reportSecurityIncident('sql_injection_attempt', { input, type });
      return false;
    }

    // Type-specific validation
    if (type in patterns && !patterns[type].test(input)) {
      return false;
    }

    return true;
  };

  const encryptSensitiveData = async (data: string): Promise<string> => {
    if (!cryptoKeyRef.current) {
      throw new Error('Encryption key not available');
    }

    try {
      const encoder = new TextEncoder();
      const iv = window.crypto.getRandomValues(new Uint8Array(12));

      const encrypted = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        cryptoKeyRef.current,
        encoder.encode(data)
      );

      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      return btoa(String.fromCharCode.apply(null, Array.from(combined)));
    } catch (error) {
      console.error('Encryption failed:', error);
      throw error;
    }
  };

  const decryptSensitiveData = async (encryptedData: string): Promise<string> => {
    if (!cryptoKeyRef.current) {
      throw new Error('Decryption key not available');
    }

    try {
      const combined = new Uint8Array(
        atob(encryptedData)
          .split('')
          .map(char => char.charCodeAt(0))
      );
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);

      const decrypted = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        cryptoKeyRef.current,
        encrypted
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw error;
    }
  };

  const checkPasswordStrength = (password: string): PasswordStrength => {
    let score = 0;
    const feedback: string[] = [];

    // Length check
    if (password.length < 8) {
      feedback.push('Password should be at least 8 characters long');
    } else if (password.length >= 12) {
      score += 2;
    } else {
      score += 1;
    }

    // Character diversity
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 2;

    // Common patterns check
    if (!/(.)\1{2,}/.test(password)) score += 1; // No repeating characters
    if (!/123456|password|qwerty|abc123/i.test(password)) score += 1; // No common passwords

    // Feedback based on missing elements
    if (!/[a-z]/.test(password)) feedback.push('Add lowercase letters');
    if (!/[A-Z]/.test(password)) feedback.push('Add uppercase letters');
    if (!/[0-9]/.test(password)) feedback.push('Add numbers');
    if (!/[^A-Za-z0-9]/.test(password)) feedback.push('Add special characters');

    const isStrong = score >= 6;

    return {
      score: Math.min(score, 8),
      feedback,
      isStrong,
    };
  };

  const scanForVulnerabilities = async (): Promise<SecurityScan> => {
    const vulnerabilities: SecurityVulnerability[] = [];
    let score = 100;

    // Check for common vulnerabilities
    if (!settings.sessionSecurity.isSecure) {
      vulnerabilities.push({
        id: generateId(),
        type: 'insecure-transport',
        severity: 'high',
        description: 'Connection is not using HTTPS',
        fix: 'Enable HTTPS for all connections',
      });
      score -= 20;
    }

    if (!settings.twoFactorAuth) {
      vulnerabilities.push({
        id: generateId(),
        type: 'weak-auth',
        severity: 'medium',
        description: 'Two-factor authentication is disabled',
        fix: 'Enable two-factor authentication',
      });
      score -= 10;
    }

    // Check for XSS vulnerabilities
    const inputElements = document.querySelectorAll('input, textarea');
    inputElements.forEach(element => {
      if (!element.hasAttribute('data-sanitized')) {
        vulnerabilities.push({
          id: generateId(),
          type: 'xss',
          severity: 'medium',
          description: 'Input field may be vulnerable to XSS',
          fix: 'Implement input sanitization',
        });
        score -= 5;
      }
    });

    const recommendations: string[] = [
      'Regularly update dependencies',
      'Implement proper input validation',
      'Use Content Security Policy headers',
      'Enable security monitoring',
      'Conduct regular security audits',
    ];

    return {
      score: Math.max(score, 0),
      vulnerabilities,
      recommendations,
    };
  };

  const enableSecurityFeature = (feature: keyof SecuritySettings) => {
    setSettings(prev => ({
      ...prev,
      [feature]: true,
    }));

    reportSecurityIncident('security_feature_enabled', { feature });
  };

  const getSecurityScore = (): number => {
    let score = 0;

    if (settings.sessionSecurity.isSecure) score += 20;
    if (settings.dataEncryption) score += 15;
    if (settings.twoFactorAuth) score += 15;
    if (settings.biometricAuth) score += 10;
    if (settings.securityAlerts) score += 10;
    if (settings.cspViolations.length === 0) score += 10;

    // Check security headers
    const requiredHeaders = Object.keys(settings.securityHeaders);
    score += (requiredHeaders.length / 5) * 20;

    return Math.min(score, 100);
  };

  // Utility functions
  const generateId = () => `sec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const generateSessionId = () => `sess_${Date.now()}_${Math.random().toString(36).substr(2, 16)}`;

  const calculateSeverity = (directive: string): CSPViolation['severity'] => {
    if (directive.includes('script-src')) return 'critical';
    if (directive.includes('object-src')) return 'high';
    if (directive.includes('style-src')) return 'medium';
    return 'low';
  };

  const reportSecurityIncident = async (type: string, data: any) => {
    try {
      // Report to security monitoring service
      await fetch('/api/security/incidents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          data,
          timestamp: Date.now(),
          sessionId: settings.sessionSecurity.sessionId,
        }),
      });
    } catch (error) {
      console.error('Failed to report security incident:', error);
    }
  };

  const contextValue: SecurityContextType = {
    settings,
    reportCSPViolation,
    validateInput,
    encryptSensitiveData,
    decryptSensitiveData,
    checkPasswordStrength,
    scanForVulnerabilities,
    enableSecurityFeature,
    getSecurityScore,
  };

  return <SecurityContext.Provider value={contextValue}>{children}</SecurityContext.Provider>;
};

// Security dashboard component
export const SecurityDashboard: React.FC = () => {
  const { settings, scanForVulnerabilities, getSecurityScore } = useSecurity();
  const [scan, setScan] = useState<SecurityScan | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  const handleScan = async () => {
    setIsScanning(true);
    try {
      const result = await scanForVulnerabilities();
      setScan(result);
    } catch (error) {
      console.error('Security scan failed:', error);
    } finally {
      setIsScanning(false);
    }
  };

  const securityScore = getSecurityScore();

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-stone-900">Security Dashboard</h1>
        <button
          onClick={handleScan}
          disabled={isScanning}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
        >
          <Shield className="w-4 h-4" />
          {isScanning ? 'Scanning...' : 'Run Security Scan'}
        </button>
      </div>

      {/* Security Score */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Security Score</h2>
          <div className={`text-3xl font-bold ${getScoreColor(securityScore)}`}>
            {securityScore}/100
          </div>
        </div>
        <div className="w-full bg-stone-200 rounded-full h-3 mt-4">
          <div
            className={`h-3 rounded-full transition-all duration-500 ${
              securityScore >= 80
                ? 'bg-green-500'
                : securityScore >= 60
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
            }`}
            style={{ width: `${securityScore}%` }}
          />
        </div>
      </div>

      {/* CSP Violations */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Recent CSP Violations</h2>
        {settings.cspViolations.length === 0 ? (
          <div className="flex items-center gap-2 text-green-600">
            <Shield className="w-4 h-4" />
            No CSP violations detected
          </div>
        ) : (
          <div className="space-y-2">
            {settings.cspViolations.slice(-5).map(violation => (
              <div
                key={violation.id}
                className="flex items-center justify-between p-3 bg-red-50 rounded-md"
              >
                <div>
                  <p className="font-medium text-red-900">{violation.directive}</p>
                  <p className="text-sm text-red-700">{violation.blockedUri}</p>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    violation.severity === 'critical'
                      ? 'bg-red-200 text-red-800'
                      : violation.severity === 'high'
                        ? 'bg-orange-200 text-orange-800'
                        : violation.severity === 'medium'
                          ? 'bg-yellow-200 text-yellow-800'
                          : 'bg-blue-200 text-blue-800'
                  }`}
                >
                  {violation.severity}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scan Results */}
      {scan && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Vulnerability Scan Results</h2>

          {scan.vulnerabilities.length === 0 ? (
            <div className="flex items-center gap-2 text-green-600">
              <Shield className="w-4 h-4" />
              No vulnerabilities found
            </div>
          ) : (
            <div className="space-y-4">
              {scan.vulnerabilities.map(vuln => (
                <div key={vuln.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-stone-900">{vuln.type}</h3>
                      <p className="text-stone-700 mt-1">{vuln.description}</p>
                      <p className="text-sm text-primary mt-2">{vuln.fix}</p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        vuln.severity === 'critical'
                          ? 'bg-red-200 text-red-800'
                          : vuln.severity === 'high'
                            ? 'bg-orange-200 text-orange-800'
                            : vuln.severity === 'medium'
                              ? 'bg-yellow-200 text-yellow-800'
                              : 'bg-blue-200 text-blue-800'
                      }`}
                    >
                      {vuln.severity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Secure input component
interface SecureInputProps {
  type: 'password' | 'text' | 'email';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  showStrength?: boolean;
  validateInput?: boolean;
}

export const SecureInput: React.FC<SecureInputProps> = ({
  type,
  value,
  onChange,
  placeholder,
  showStrength = false,
  validateInput = true,
}) => {
  const { validateInput: securityValidate, checkPasswordStrength } = useSecurity();
  const [showPassword, setShowPassword] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [strength, setStrength] = useState<PasswordStrength | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (validateInput) {
      const valid = securityValidate(newValue, type === 'email' ? 'email' : 'url');
      setIsValid(valid);
    }

    if (type === 'password' && showStrength) {
      const passwordStrength = checkPasswordStrength(newValue);
      setStrength(passwordStrength);
    }

    onChange(newValue);
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <input
          type={type === 'password' && !showPassword ? 'password' : 'text'}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
            !isValid ? 'border-red-500 focus:ring-red-500' : 'border-stone-300 focus:ring-ring'
          }`}
          data-sanitized="true"
        />

        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-500 hover:text-stone-700"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>

      {!isValid && <p className="text-sm text-red-600">Invalid input detected</p>}

      {strength && type === 'password' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Strength:</span>
            <div className="flex gap-1">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className={`w-4 h-2 rounded-full ${
                    i < strength.score / 2
                      ? strength.isStrong
                        ? 'bg-green-500'
                        : 'bg-yellow-500'
                      : 'bg-stone-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {strength.feedback.length > 0 && (
            <ul className="text-xs text-stone-600 list-disc list-inside">
              {strength.feedback.map((feedback, i) => (
                <li key={i}>{feedback}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SecurityProvider;
